import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Card,
  Button,
  Avatar,
  Select,
  Switch,
  message,
  Spin,
  Alert,
  Modal,
  Form,
  Input,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CameraOutlined,
  ShopOutlined,
  TagOutlined,
  AppstoreOutlined,
  TeamOutlined,
  BarChartOutlined,
  SettingOutlined,
  EditOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { formatVnDate } from '../utils/vnDateTime';
import { useAuth } from '../contexts/AuthContext';
import {
  canUpdatePermissions,
  canDeactivateAdmin,
} from '../utils/adminPermissions';
import {
  FULL_PERMISSIONS,
  DEFAULT_ADMIN_PERMISSIONS,
  DEFAULT_SUPPORT_PERMISSIONS,
  type PermissionsMap,
  type PermissionLevel,
} from '../constants/adminPermissions';

const CARD_STYLE = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};
const CARD_BODY = { padding: 24 };

interface AdminDetail {
  id: number;
  fullName: string;
  email: string;
  role: string;
  permissions: string[] | Record<string, string>;
  status: string;
  lastLoginAt: string | null;
  inviteSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AdminDetailResponse {
  success: boolean;
  data: AdminDetail;
}

const PERMISSION_MODULES: {
  key: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}[] = [
  {
    key: 'merchants',
    title: 'Merchants',
    subtitle: 'Manage vendors',
    icon: <ShopOutlined />,
  },
  {
    key: 'vouchers',
    title: 'Vouchers',
    subtitle: 'Promo codes',
    icon: <TagOutlined />,
  },
  {
    key: 'menu_items',
    title: 'Menu Items',
    subtitle: 'Products & Pricing',
    icon: <AppstoreOutlined />,
  },
  {
    key: 'user_mgmt',
    title: 'User Mgmt',
    subtitle: 'Admin accounts',
    icon: <TeamOutlined />,
  },
  {
    key: 'reports',
    title: 'Reports',
    subtitle: 'Analytics',
    icon: <BarChartOutlined />,
  },
  {
    key: 'settings',
    title: 'Settings',
    subtitle: 'System config',
    icon: <SettingOutlined />,
  },
];

function getRoleLabel(role: string): string {
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'admin') return 'Admin';
  if (role === 'support') return 'Support';
  if (role === 'editor') return 'Editor';
  if (role === 'viewer') return 'Viewer';
  return role;
}

function getRoleBadgeClass(role: string): string {
  if (role === 'super_admin')
    return 'admin-detail-role-badge admin-detail-role-badge-purple';
  if (role === 'admin' || role === 'editor')
    return 'admin-detail-role-badge admin-detail-role-badge-blue';
  return 'admin-detail-role-badge admin-detail-role-badge-gray';
}

function getPermissionLevel(
  moduleKey: string,
  role: string,
  permissions: string[] | Record<string, string> | null | undefined
): PermissionLevel {
  // Object format: { merchants: 'write', vouchers: 'read', ... }
  if (
    permissions &&
    typeof permissions === 'object' &&
    !Array.isArray(permissions)
  ) {
    const val = (permissions as Record<string, string>)[moduleKey];
    if (val === 'write' || val === 'read' || val === 'none')
      return val as PermissionLevel;
  }
  // Legacy array format (Full Access, etc.)
  const perms = Array.isArray(permissions) ? permissions : [];
  const hasFull = perms.some((p: unknown) =>
    String(p || '')
      .toLowerCase()
      .includes('full access')
  );
  if (role === 'super_admin' || hasFull) return 'write';
  if (role === 'support' || role === 'viewer') {
    if (moduleKey === 'reports') return 'read';
    if (['merchants', 'vouchers', 'menu_items'].includes(moduleKey))
      return 'read';
    return 'none';
  }
  if (role === 'admin' || role === 'editor') {
    if (moduleKey === 'settings') return 'none';
    if (moduleKey === 'user_mgmt') return 'read';
    if (['merchants', 'vouchers', 'menu_items'].includes(moduleKey))
      return 'write';
    if (moduleKey === 'reports') return 'read';
    return 'none';
  }
  return 'none';
}

function getPermissionBadgeClass(level: PermissionLevel | string): string {
  if (level === 'write')
    return 'admin-detail-perm-badge admin-detail-perm-badge-write';
  if (level === 'read')
    return 'admin-detail-perm-badge admin-detail-perm-badge-read';
  if (level === 'restricted')
    return 'admin-detail-perm-badge admin-detail-perm-badge-restricted';
  return 'admin-detail-perm-badge admin-detail-perm-badge-none';
}

export default function AdminManagementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [admin, setAdmin] = useState<AdminDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [twoFa, setTwoFa] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [resetSessionLoading, setResetSessionLoading] = useState(false);
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [permissionsFormState, setPermissionsFormState] =
    useState<PermissionsMap>({});

  const loadAdmin = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<AdminDetailResponse>(`/admin/admins/${id}`);
      const body = res.data;
      if (body.success && body.data) {
        setAdmin(body.data);
      } else {
        setError('Admin not found');
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (ax.response?.status === 404) {
        setError('Admin not found');
      } else {
        setError(ax.response?.data?.message || 'Failed to load admin');
      }
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const stateAdmin = (location.state as { admin?: AdminDetail } | null)
      ?.admin;
    if (stateAdmin && String(stateAdmin.id) === String(id)) {
      setAdmin(stateAdmin);
      setLoading(false);
      setError(null);
      return;
    }
    loadAdmin();
  }, [id, loadAdmin, location.state]);

  const handleDeactivate = async () => {
    if (!admin || admin.status === 'INACTIVE') return;
    try {
      setDeactivating(true);
      await api.patch(`/admin/admins/${admin.id}/status`, {
        status: 'INACTIVE',
      });
      message.success('Admin deactivated');
      setAdmin(prev => (prev ? { ...prev, status: 'INACTIVE' } : null));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to deactivate');
    } finally {
      setDeactivating(false);
    }
  };

  const openEditModal = () => {
    if (admin) {
      editForm.setFieldsValue({
        fullName: admin.fullName || admin.email || '',
        email: admin.email || '',
      });
      setEditModalVisible(true);
    }
  };

  const handleEditProfileSubmit = async () => {
    if (!admin) return;
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      const res = await api.patch(`/admin/admins/${admin.id}`, {
        fullName: values.fullName,
        email: values.email,
      });
      if (res.data?.success && res.data?.data) {
        setAdmin(prev => (prev ? { ...prev, ...res.data.data } : null));
        message.success('Profile updated');
        setEditModalVisible(false);
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to update profile');
    } finally {
      setEditSaving(false);
    }
  };

  const openPasswordModal = () => {
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handleChangePasswordSubmit = async () => {
    if (!admin) return;
    try {
      const values = await passwordForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error('Passwords do not match');
        return;
      }
      setPasswordSaving(true);
      await api.post(`/admin/admins/${admin.id}/reset-password`, {
        newPassword: values.newPassword,
      });
      message.success('Password has been reset');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to reset password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleResetSession = () => {
    if (!admin) return;
    Modal.confirm({
      title: 'Reset Session',
      content:
        'This will ask the admin to log in again. Do you want to continue?',
      okText: 'Reset',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setResetSessionLoading(true);
          await api.post(`/admin/admins/${admin.id}/reset-session`);
          message.success('Session reset requested');
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string } } };
          message.error(
            ax.response?.data?.message || 'Failed to reset session'
          );
        } finally {
          setResetSessionLoading(false);
        }
      },
    });
  };

  const handleTwoFaChange = (checked: boolean) => {
    setTwoFa(checked);
    message.info('2-Step Verification is coming soon');
    if (checked) setTwoFa(false); // revert until feature is implemented
  };

  const actor = currentUser
    ? { id: currentUser.id, role: currentUser.role }
    : null;
  const canShowUpdatePermissions =
    !!admin &&
    !!actor &&
    canUpdatePermissions(actor, { id: admin.id, role: admin.role });
  const canShowDeactivate =
    !!admin &&
    !!actor &&
    admin.status !== 'INACTIVE' &&
    canDeactivateAdmin(actor, { id: admin.id, role: admin.role });

  const openPermissionsModal = () => {
    if (admin) {
      const perms =
        admin.permissions &&
        typeof admin.permissions === 'object' &&
        !Array.isArray(admin.permissions)
          ? { ...admin.permissions }
          : {};
      PERMISSION_MODULES.forEach(mod => {
        if (!(mod.key in perms)) perms[mod.key] = 'none';
      });
      setPermissionsFormState(perms as PermissionsMap);
      setPermissionsModalVisible(true);
    }
  };

  const applyTemplate = (
    template: Record<string, PermissionLevel | string>
  ) => {
    setPermissionsFormState(template as PermissionsMap);
  };

  const handlePermissionsSave = async () => {
    if (!admin) return;
    try {
      setPermissionsSaving(true);
      const res = await api.patch(`/admin/admins/${admin.id}/permissions`, {
        permissions: permissionsFormState,
      });
      if (res.data?.success && res.data?.data) {
        setAdmin(prev =>
          prev ? { ...prev, permissions: res.data.data.permissions } : null
        );
        message.success('Permissions updated');
        setPermissionsModalVisible(false);
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(
        ax.response?.data?.message || 'Failed to update permissions'
      );
    } finally {
      setPermissionsSaving(false);
    }
  };

  if (loading && !admin) {
    return (
      <div className="admin-detail-wrap">
        <div className="admin-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="admin-detail-breadcrumb-sep">&gt;</span>
          <Link to="/admin-management">Admin Management</Link>
          <span className="admin-detail-breadcrumb-sep">&gt;</span>
          <span>...</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error && !admin) {
    return (
      <div className="admin-detail-wrap">
        <div className="admin-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="admin-detail-breadcrumb-sep">&gt;</span>
          <Link to="/admin-management">Admin Management</Link>
        </div>
        <Alert
          message={error}
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/admin-management')}>
              Back to list
            </Button>
          }
        />
      </div>
    );
  }

  if (!admin) return null;

  const roleLabel = getRoleLabel(admin.role);
  const roleBadgeClass = getRoleBadgeClass(admin.role);

  return (
    <div className="admin-detail-wrap">
      <div className="admin-detail-breadcrumb">
        <Link to="/">Dashboard</Link>
        <span className="admin-detail-breadcrumb-sep">&gt;</span>
        <Link to="/admin-management">Admin Management</Link>
        <span className="admin-detail-breadcrumb-sep">&gt;</span>
        <span className="admin-detail-breadcrumb-current">
          {admin.fullName || admin.email || 'Admin'}
        </span>
      </div>

      {/* Profile Header Card */}
      <Card
        style={CARD_STYLE}
        bodyStyle={CARD_BODY}
        className="admin-detail-profile-card"
      >
        <div className="admin-detail-profile-inner">
          <div className="admin-detail-profile-left">
            <div className="admin-detail-avatar-wrap">
              <Avatar
                size={80}
                icon={<UserOutlined />}
                className="admin-detail-avatar"
                style={{
                  backgroundColor:
                    admin.status === 'PENDING' ? '#fef9c3' : '#e5e7eb',
                  color: '#6b7280',
                }}
              />
              <span className="admin-detail-avatar-camera" aria-hidden>
                <CameraOutlined />
              </span>
            </div>
            <div className="admin-detail-profile-info">
              <h1 className="admin-detail-name">
                {admin.fullName || admin.email || '–'}
              </h1>
              <span className={roleBadgeClass}>{roleLabel}</span>
            </div>
          </div>
          <div className="admin-detail-profile-actions">
            {canShowUpdatePermissions && (
              <Button
                icon={<SafetyCertificateOutlined />}
                onClick={openPermissionsModal}
                className="admin-detail-btn-secondary"
              >
                Update Permissions
              </Button>
            )}
            {canShowDeactivate && (
              <Button
                loading={deactivating}
                onClick={handleDeactivate}
                className="admin-detail-btn-secondary"
              >
                Deactivate
              </Button>
            )}
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={openEditModal}
              className="admin-detail-btn-primary"
            >
              Edit Profile
            </Button>
          </div>
        </div>
      </Card>

      <div className="admin-detail-grid">
        <div className="admin-detail-col-left">
          {/* Personal Information */}
          <Card
            style={CARD_STYLE}
            bodyStyle={CARD_BODY}
            className="admin-detail-card"
          >
            <div className="admin-detail-card-head">
              <h2 className="admin-detail-card-title">Personal Information</h2>
              <button
                type="button"
                className="admin-detail-edit-link"
                onClick={openEditModal}
              >
                Edit
              </button>
            </div>
            <div className="admin-detail-field">
              <MailOutlined className="admin-detail-field-icon" />
              <div>
                <div className="admin-detail-field-label">EMAIL ADDRESS</div>
                <div className="admin-detail-field-value">
                  {admin.email || '–'}
                </div>
              </div>
            </div>
            <div className="admin-detail-field">
              <PhoneOutlined className="admin-detail-field-icon" />
              <div>
                <div className="admin-detail-field-label">PHONE NUMBER</div>
                <div className="admin-detail-field-value">–</div>
              </div>
            </div>
            <div className="admin-detail-field">
              <CalendarOutlined className="admin-detail-field-icon" />
              <div>
                <div className="admin-detail-field-label">JOIN DATE</div>
                <div className="admin-detail-field-value">
                  {formatVnDate(admin.createdAt, 'MMMM D, YYYY')}
                </div>
              </div>
            </div>
          </Card>

          {/* Account Security */}
          <Card
            style={CARD_STYLE}
            bodyStyle={CARD_BODY}
            className="admin-detail-card"
          >
            <h2 className="admin-detail-card-title">Account Security</h2>
            <div className="admin-detail-security-row">
              <div>
                <div className="admin-detail-security-label">Password</div>
                <div className="admin-detail-security-meta">Last changed –</div>
              </div>
              <button
                type="button"
                className="admin-detail-edit-link"
                onClick={openPasswordModal}
              >
                Change
              </button>
            </div>
            <div className="admin-detail-security-row">
              <div>
                <div className="admin-detail-security-label">
                  2-Step Verification
                </div>
                <div className="admin-detail-security-meta">
                  Secure your account with 2FA
                </div>
              </div>
              <Switch
                checked={twoFa}
                onChange={handleTwoFaChange}
                style={{ flexShrink: 0 }}
              />
            </div>
            <Button
              icon={<ReloadOutlined />}
              danger
              loading={resetSessionLoading}
              onClick={handleResetSession}
              className="admin-detail-reset-session"
            >
              Reset Session
            </Button>
          </Card>
        </div>

        <div className="admin-detail-col-right">
          {/* Permissions & Access */}
          <Card
            style={CARD_STYLE}
            bodyStyle={CARD_BODY}
            className="admin-detail-card"
          >
            <h2 className="admin-detail-card-title">Permissions & Access</h2>
            <div className="admin-detail-permissions-grid">
              {PERMISSION_MODULES.map(mod => {
                const level = getPermissionLevel(
                  mod.key,
                  admin.role,
                  admin.permissions
                );
                const badgeLabel =
                  level === 'write'
                    ? 'Write'
                    : level === 'read'
                    ? 'Read'
                    : 'None';
                return (
                  <div key={mod.key} className="admin-detail-perm-box">
                    <div className="admin-detail-perm-box-icon">{mod.icon}</div>
                    <div className="admin-detail-perm-box-text">
                      <div className="admin-detail-perm-box-title">
                        {mod.title}
                      </div>
                      <div className="admin-detail-perm-box-subtitle">
                        {mod.subtitle}
                      </div>
                    </div>
                    <span className={getPermissionBadgeClass(level)}>
                      {badgeLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Recent Activity */}
          <Card
            style={CARD_STYLE}
            bodyStyle={CARD_BODY}
            className="admin-detail-card"
          >
            <div className="admin-detail-card-head">
              <h2 className="admin-detail-card-title">Recent Activity</h2>
              <Select
                defaultValue="30"
                className="admin-detail-activity-select"
                options={[
                  { value: '7', label: 'Last 7 Days' },
                  { value: '30', label: 'Last 30 Days' },
                  { value: '90', label: 'Last 90 Days' },
                ]}
              />
            </div>
            <div className="admin-detail-activity-list">
              {/* No activity fields in API; show empty state */}
              <div className="admin-detail-activity-empty">
                No recent activity
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        title="Edit Profile"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleEditProfileSubmit}
        confirmLoading={editSaving}
        destroyOnClose
        okText="Save"
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input type="email" placeholder="Email" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Update Permissions Modal */}
      <Modal
        title="Update Permissions"
        open={permissionsModalVisible}
        onCancel={() => setPermissionsModalVisible(false)}
        onOk={handlePermissionsSave}
        confirmLoading={permissionsSaving}
        destroyOnClose
        okText="Save changes"
        width={560}
      >
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Button
              size="small"
              onClick={() => applyTemplate(DEFAULT_SUPPORT_PERMISSIONS)}
            >
              Support default
            </Button>
            <Button
              size="small"
              onClick={() => applyTemplate(DEFAULT_ADMIN_PERMISSIONS)}
            >
              Admin default
            </Button>
            <Button
              size="small"
              onClick={() => applyTemplate(FULL_PERMISSIONS)}
            >
              Full access
            </Button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PERMISSION_MODULES.map(mod => (
              <div
                key={mod.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{mod.title}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {mod.subtitle}
                  </div>
                </div>
                <Select
                  value={permissionsFormState[mod.key] || 'none'}
                  onChange={v =>
                    setPermissionsFormState(p => ({ ...p, [mod.key]: v }))
                  }
                  style={{ width: 120 }}
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'read', label: 'Read' },
                    { value: 'write', label: 'Write' },
                  ]}
                />
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title="Reset Password"
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        onOk={handleChangePasswordSubmit}
        confirmLoading={passwordSaving}
        destroyOnClose
        okText="Reset Password"
      >
        <Form form={passwordForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
